import { useState, useEffect } from "react"
import { X, Bookmark, MapPin, Clock, Briefcase, DollarSign, Globe, Code2, ExternalLink, Ban, ArrowRight } from "lucide-react"
import { daysAgoLabel, initials, parseSalary } from "../JobCard"

const FF = "'Plus Jakarta Sans', 'Inter', sans-serif"
const BRANDFETCH_CLIENT_ID = "1idn4glWklGs5yLvUdW"

function Sk({ w = "100%", h = 14 }) {
  return <div style={{ width: w, height: h, borderRadius: 6, background: "#eee", animation: "jdesc-shimmer 1.4s ease-in-out infinite" }} />
}

// Each fact row (icon + label)
function FactRow({ icon, label }) {
  if (!label) return null
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, fontFamily: FF }}>
      <span style={{ display: "flex", color: "#52525b", flexShrink: 0 }}>{icon}</span>
      <span style={{ fontSize: 13, fontWeight: 500, color: "#18181b" }}>{label}</span>
    </div>
  )
}

export default function JobDescPanel({
  job, loading, savedJobIds, onHeartToggle, onApply, onPass, onClose, open,
}) {
  const [saved,   setSaved]   = useState(false)
  const [liking,  setLiking]  = useState(false)
  const [passing, setPassing] = useState(false)

  useEffect(() => {
    if (job) setSaved(savedJobIds?.has(job.id || job.job_id) ?? false)
  }, [job, savedJobIds])

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden"
    else document.body.style.overflow = ""
    return () => { document.body.style.overflow = "" }
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const h = (e) => { if (e.key === "Escape") onClose?.() }
    document.addEventListener("keydown", h)
    return () => document.removeEventListener("keydown", h)
  }, [open, onClose])

  const handleSave = () => {
    if (liking) return
    if (saved) { setSaved(false); onHeartToggle?.(job); return }
    setLiking(true)
    setTimeout(() => { setLiking(false); setSaved(true); onHeartToggle?.(job) }, 500)
  }

  const handleApply = () => {
    if (!job) return
    onApply?.(job)
  }

  const handlePass = () => {
    if (!job) return
    setPassing(true)
    setTimeout(() => { setPassing(false); onPass?.(job); onClose?.() }, 150)
  }

  const sal = (() => {
    if (!job) return null
    const min = job.salary_min, max = job.salary_max
    if (min && max) return `USD ${Math.round(min / 1000)}k – ${Math.round(max / 1000)}k /yr`
    if (!min && !max) return parseSalary(job.salary_range) || null
    return null
  })()

  const level = job?.seniority_key && job.seniority_key !== "UNKNOWN"
    ? job.seniority_key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
    : null

  const skills = Array.isArray(job?.skills) ? job.skills : []
  const description = job?.description || ""
  const isHtml = /<[a-z][\s\S]*>/i.test(description)
  const company     = job?.company || "—"
  const title       = job?.role || "—"
  const ago         = job ? daysAgoLabel(job) : ""
  const sourceLabel = job?.source ? (job.source === "Custom" ? `${job.company || "Company"} Careers` : job.source) : null
  const location    = job?.canonical_location_id || job?.location || null
  const workType    = job?.work_type || null

  const domain = company
    ? company.toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9]/g, "") + ".com"
    : null

  return (
    <>
      <style>{`
        @keyframes jdesc-shimmer { 0%,100%{opacity:1} 50%{opacity:0.5} }
        @keyframes jdesc-slide-in { from{transform:translateX(100%)} to{transform:translateX(0)} }
        @keyframes jdesc-fade-in { from{opacity:0} to{opacity:1} }
        .jdesc-scroll::-webkit-scrollbar { width: 4px }
        .jdesc-scroll::-webkit-scrollbar-track { background: transparent }
        .jdesc-scroll::-webkit-scrollbar-thumb { background: #d4d4d8; border-radius: 2px }
        .jdesc-body { font-size: 13.5px; color: #3f3f46; line-height: 1.7; font-family: ${FF}; word-break: break-word; }
        .jdesc-body h1,.jdesc-body h2,.jdesc-body h3,.jdesc-body h4 {
          font-size: 14px; font-weight: 700; color: #18181b;
          margin: 18px 0 6px; letter-spacing: -0.01em; line-height: 1.3;
        }
        .jdesc-body p  { margin: 0 0 10px; }
        .jdesc-body ul,.jdesc-body ol { padding-left: 18px; margin: 0 0 12px; }
        .jdesc-body li { margin-bottom: 5px; }
        .jdesc-body strong,.jdesc-body b { color: #18181b; font-weight: 700; }
        .jdesc-body a  { color: #18181b; text-decoration: underline; }
        .jdesc-body mark { background: #FFE889; color: #18181b; padding: 0 2px; border-radius: 2px; }
        .jdesc-apply-cta-arrow { transition: transform 0.2s; }
        .jdesc-apply-cta:hover .jdesc-apply-cta-arrow { transform: translateX(2px); }
      `}</style>

      {/* Scrim */}
      {open && (
        <div
          onClick={onClose}
          style={{
            position: "fixed", inset: 0, zIndex: 400,
            background: "rgba(0,0,0,0.35)",
            animation: "jdesc-fade-in 0.2s ease",
          }}
        />
      )}

      {/* Drawer */}
      <aside
        aria-hidden={!open}
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          width: "min(560px, 100vw)",
          background: "#fff",
          zIndex: 401,
          boxShadow: "-24px 0 48px rgba(0,0,0,0.12)",
          display: "flex", flexDirection: "column",
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.28s cubic-bezier(0.22,1,0.36,1)",
          fontFamily: FF,
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "20px 24px 16px", gap: 14,
          borderBottom: "1px solid #f0f0f0",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0, flex: 1 }}>
            {/* Big company logo */}
            <div style={{
              width: 50, height: 50, borderRadius: 10,
              background: "#f4f4f5",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0, overflow: "hidden",
            }}>
              {domain ? (
                <img
                  src={`https://cdn.brandfetch.io/${domain}?c=${BRANDFETCH_CLIENT_ID}`}
                  alt={company}
                  onError={e => {
                    e.currentTarget.style.display = "none"
                    e.currentTarget.parentElement.setAttribute("data-fallback", "1")
                  }}
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : (
                <span style={{ fontSize: 18, fontWeight: 700, color: "#52525b" }}>
                  {initials(company)}
                </span>
              )}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <h2 style={{
                margin: 0, fontSize: 20, fontWeight: 700,
                letterSpacing: "-0.025em", color: "#18181b", lineHeight: 1.25,
              }}>
                {title}
              </h2>
              <p style={{
                margin: "4px 0 0", fontSize: 13, color: "#52525b", fontWeight: 500,
              }}>
                {company}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: "transparent", border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#52525b", flexShrink: 0,
              transition: "background 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "#f4f4f5"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="jdesc-scroll" style={{ flex: 1, overflowY: "auto", padding: "18px 24px 24px" }}>
          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Sk w="90%" h={16} />
              <Sk w="50%" h={14} />
              <div style={{ height: 20 }} />
              {[...Array(6)].map((_, i) => <Sk key={i} w={i % 3 === 2 ? "60%" : "100%"} h={13} />)}
            </div>
          ) : job ? (
            <>
              {/* Facts grid (2 cols) */}
              <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 20px",
                marginBottom: 20,
              }}>
                <FactRow icon={<MapPin size={14} strokeWidth={2} />} label={location} />
                <FactRow icon={<DollarSign size={14} strokeWidth={2} />} label={sal} />
                <FactRow icon={<Briefcase size={14} strokeWidth={2} />} label={level} />
                <FactRow icon={<Globe size={14} strokeWidth={2} />} label={workType} />
                <FactRow icon={<Clock size={14} strokeWidth={2} />} label={job.experience_min != null ? `${job.experience_min}+ yrs exp` : "Full Time"} />
                <FactRow icon={<Code2 size={14} strokeWidth={2} />} label={sourceLabel} />
              </div>

              {/* Skills */}
              {skills.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <p style={{
                    margin: "0 0 10px", fontSize: 13, fontWeight: 700,
                    color: "#18181b", letterSpacing: "-0.01em",
                  }}>
                    Skills & Technologies
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {skills.map((s, i) => (
                      <span key={i} style={{
                        display: "inline-flex", alignItems: "center",
                        padding: "5px 10px", borderRadius: 3,
                        fontSize: 12, fontWeight: 500, fontFamily: FF,
                        color: "#5b21b6",
                        background: "#ede9fe",
                        border: "none",
                        whiteSpace: "nowrap",
                      }}>
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              <div>
                <p style={{
                  margin: "0 0 12px", fontSize: 13, fontWeight: 700,
                  color: "#18181b", letterSpacing: "-0.01em",
                }}>
                  Description <span style={{ color: "#a1a1aa", fontWeight: 400 }}>(highlighting: Workday)</span>
                </p>
                {isHtml ? (
                  <div className="jdesc-body" dangerouslySetInnerHTML={{ __html: highlightKeywords(description) }} />
                ) : (
                  <div className="jdesc-body" style={{ whiteSpace: "pre-wrap" }}
                    dangerouslySetInnerHTML={{ __html: highlightKeywords(escapeHtml(description || "No description available.")) }}
                  />
                )}
              </div>
            </>
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
              <p style={{ fontSize: 13, color: "#71717a" }}>Select a job to view details</p>
            </div>
          )}
        </div>

        {/* Sticky footer */}
        {job && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "12px 20px",
            borderTop: "1px solid #f0f0f0",
            background: "#fff",
            flexShrink: 0, gap: 8,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button
                onClick={() => job.job_url && window.open(job.job_url, "_blank")}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  background: "none", border: "none", cursor: "pointer",
                  padding: "8px 10px", borderRadius: 8,
                  fontSize: 12, fontWeight: 500, color: "#52525b", fontFamily: FF,
                  transition: "background 0.12s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#f4f4f5"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <ExternalLink size={13} strokeWidth={2} />
                View original posting
              </button>
              <button
                onClick={handlePass}
                disabled={passing}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  background: "none", border: "none", cursor: passing ? "default" : "pointer",
                  padding: "8px 10px", borderRadius: 8,
                  fontSize: 12, fontWeight: 500, color: "#71717a", fontFamily: FF,
                  transition: "background 0.12s",
                }}
                onMouseEnter={e => { if (!passing) e.currentTarget.style.background = "#f4f4f5" }}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <Ban size={13} strokeWidth={2} />
                Not Interested
              </button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button
                onClick={handleSave}
                aria-label={saved ? "Saved" : "Save"}
                style={{
                  width: 36, height: 36, borderRadius: 8,
                  background: saved ? "#f4f4f5" : "transparent",
                  border: "1px solid #e4e4e7", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "background 0.12s",
                }}
                onMouseEnter={e => { if (!saved) e.currentTarget.style.background = "#f4f4f5" }}
                onMouseLeave={e => { if (!saved) e.currentTarget.style.background = "transparent" }}
              >
                <Bookmark size={14} strokeWidth={2}
                  fill={saved ? "#18181b" : "none"}
                  stroke="#18181b" />
              </button>
              <button
                onClick={handleApply}
                className="jdesc-apply-cta"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  background: "#121212", color: "#fff", border: "none",
                  borderRadius: 9999, height: 40, padding: "0 20px",
                  fontSize: 13, fontWeight: 600, fontFamily: FF, cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#000"}
                onMouseLeave={e => e.currentTarget.style.background = "#121212"}
              >
                Apply
                <ArrowRight size={14} strokeWidth={2.4} className="jdesc-apply-cta-arrow" />
              </button>
            </div>
          </div>
        )}
      </aside>
    </>
  )
}

// Minimal keyword highlighter — wraps occurrences of the company source in <mark>
function escapeHtml(s) {
  return s.replace(/[&<>"']/g, c => ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;" })[c])
}
function highlightKeywords(html) {
  // highlight the word "Workday" (and a few generic ones). Case-insensitive, whole-ish word.
  const terms = ["Workday"]
  let out = html
  for (const t of terms) {
    const re = new RegExp(`(?<![\\w<])(${t})(?![\\w>])`, "gi")
    out = out.replace(re, "<mark>$1</mark>")
  }
  return out
}
